import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, Eye, EyeSlash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdsPage() {
  const [ads, setAds] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", image_url: "", link_url: "", open_blank: true, position: 0 });
  const [editingId, setEditingId] = useState(null);
  const [globalJs, setGlobalJs] = useState([""]);
  const [legalTemplate, setLegalTemplate] = useState("");

  const fetchAds = async () => {
    try { const { data } = await axios.get(`${API}/ads`, { withCredentials: true }); setAds(data); } catch {}
  };
  const fetchSettings = async () => {
    try { const { data } = await axios.get(`${API}/global-settings`, { withCredentials: true }); setGlobalJs(data.global_js_urls?.length ? data.global_js_urls : [""]); setLegalTemplate(data.legal_footer_template || ""); } catch {}
  };

  useEffect(() => { fetchAds(); fetchSettings(); }, []);

  const handleSave = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/ads/${editingId}`, form, { withCredentials: true });
        toast.success("Ad updated");
      } else {
        await axios.post(`${API}/ads`, { ...form, active: true }, { withCredentials: true });
        toast.success("Ad created");
      }
      setDialog(false); setEditingId(null); setForm({ title: "", image_url: "", link_url: "", open_blank: true, position: 0 }); fetchAds();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this ad?")) return;
    try { await axios.delete(`${API}/ads/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchAds(); } catch { toast.error("Failed"); }
  };

  const toggleActive = async (ad) => {
    try { await axios.put(`${API}/ads/${ad.id}`, { active: !ad.active }, { withCredentials: true }); fetchAds(); } catch {}
  };

  const moveAd = async (ad, direction) => {
    const newPos = ad.position + direction;
    try { await axios.put(`${API}/ads/${ad.id}`, { position: newPos }, { withCredentials: true }); fetchAds(); } catch {}
  };

  const openEdit = (ad) => {
    setEditingId(ad.id);
    setForm({ title: ad.title || "", image_url: ad.image_url, link_url: ad.link_url, open_blank: ad.open_blank, position: ad.position });
    setDialog(true);
  };

  const saveGlobalSettings = async () => {
    try {
      await axios.put(`${API}/global-settings`, { global_js_urls: globalJs.filter(u => u.trim()), legal_footer_template: legalTemplate }, { withCredentials: true });
      toast.success("Global settings saved");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="ads-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Ads & Global Settings</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Manage advertisements and global JS/legal settings for all sub-sites</p>
      </div>

      {/* Ads Section */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center justify-between">
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Advertisements</h3>
          <Button data-testid="add-ad-btn" onClick={() => { setEditingId(null); setForm({ title: "", image_url: "", link_url: "", open_blank: true, position: ads.length }); setDialog(true); }} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-8 px-3 text-xs font-semibold"><Plus size={12} className="mr-1" /> Add Ad</Button>
        </div>
        <div className="divide-y divide-[#2A2A2E]">
          {ads.length === 0 ? <div className="px-5 py-8 text-center text-sm text-[#555]">No ads yet</div> : ads.map(ad => (
            <div key={ad.id} className={`px-5 py-3 flex items-center gap-4 ${!ad.active ? 'opacity-40' : ''}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveAd(ad, -1)} className="text-[#555] hover:text-white"><ArrowUp size={12} /></button>
                <span className="text-[10px] text-[#555] text-center">{ad.position}</span>
                <button onClick={() => moveAd(ad, 1)} className="text-[#555] hover:text-white"><ArrowDown size={12} /></button>
              </div>
              {ad.image_url && <img src={ad.image_url} alt="" className="w-24 h-14 object-cover rounded-sm bg-[#0A0A0C] border border-[#2A2A2E] flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium">{ad.title || "Untitled"}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{ad.link_url}</div>
                <div className="text-[10px] text-[#555]">{ad.open_blank ? "Opens in new tab" : "Same tab"}</div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`toggle-ad-${ad.id}`} onClick={() => toggleActive(ad)} className="p-1.5 text-[#A1A1AA] hover:text-white transition-colors" title={ad.active ? "Deactivate" : "Activate"}>
                  {ad.active ? <Eye size={14} /> : <EyeSlash size={14} />}
                </button>
                <button onClick={() => openEdit(ad)} className="p-1.5 text-[#A1A1AA] hover:text-[#4361EE] transition-colors"><PencilSimple size={14} /></button>
                <button onClick={() => handleDelete(ad.id)} className="p-1.5 text-[#A1A1AA] hover:text-[#E53935] transition-colors"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Global JS URLs */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
        <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">Global JS URLs</h3>
        <p className="text-xs text-[#555] mb-3">These script URLs will be injected into ALL sub-project sites. Use for popup/hit networks, analytics, etc.</p>
        <div className="space-y-2">
          {globalJs.map((url, i) => (
            <div key={i} className="flex gap-2">
              <Input value={url} onChange={e => { const n = [...globalJs]; n[i] = e.target.value; setGlobalJs(n); }} placeholder="https://example.com/script.js" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs flex-1" />
              <button onClick={() => setGlobalJs(globalJs.filter((_, j) => j !== i))} className="p-2 text-[#A1A1AA] hover:text-[#E53935]"><Trash size={14} /></button>
            </div>
          ))}
          <Button variant="ghost" onClick={() => setGlobalJs([...globalJs, ""])} className="text-[#4361EE] text-xs h-7"><Plus size={12} className="mr-1" /> Add URL</Button>
        </div>
      </div>

      {/* Legal Footer Template */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5">
        <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA] mb-3">Legal Footer Template</h3>
        <p className="text-xs text-[#555] mb-3">Use variables: {"{{SITE_NAME}}"}, {"{{DOMAIN}}"}, {"{{YEAR}}"}. Applied to all sub-project sites.</p>
        <textarea data-testid="legal-template-input" value={legalTemplate} onChange={e => setLegalTemplate(e.target.value)} placeholder="All content on {{SITE_NAME}} is provided for informational purposes. &copy; {{YEAR}} {{DOMAIN}}. Users must be 18+." className="w-full bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm p-3 h-24 text-xs resize-y" />
      </div>

      <Button data-testid="save-global-btn" onClick={saveGlobalSettings} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-10 px-8 font-semibold">Save Global Settings</Button>

      {/* Ad Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-md rounded-sm">
          <DialogHeader><DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>{editingId ? "Edit Ad" : "New Ad"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Title</label><Input data-testid="ad-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Image URL</label><Input data-testid="ad-image" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Link URL</label><Input data-testid="ad-link" value={form.link_url} onChange={e => setForm({...form, link_url: e.target.value})} placeholder="https://..." className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Position</label><Input data-testid="ad-position" type="number" value={form.position} onChange={e => setForm({...form, position: parseInt(e.target.value) || 0})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer"><input type="checkbox" checked={form.open_blank} onChange={e => setForm({...form, open_blank: e.target.checked})} /> Open in new tab</label></div>
            </div>
            {form.image_url && <img src={form.image_url} alt="Preview" className="w-full h-24 object-cover rounded-sm bg-[#0A0A0C] border border-[#2A2A2E]" />}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setDialog(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
              <Button data-testid="ad-save-btn" onClick={handleSave} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm px-6 font-semibold">{editingId ? "Update" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
